import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    Output
} from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ScreenAnalyticsDirective } from '@flyfreely-portal-ui/analytics';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    BatteryDto,
    BatteryService,
    BatterySetDto,
    BatterySetService,
    Cancellable,
    FlyFreelyError,
    FlyFreelyLoggingService,
    LockedBatterySetFields,
    PersonsOrganisationDto,
    UpdateBatterySetCommand,
    WorkTracker,
    fromLocalDate,
    toLocalDate
} from '@flyfreely-portal-ui/flyfreely';
import { batterySearch } from '@flyfreely-portal-ui/resource-ui';
import * as moment from 'moment';
import { Subject, combineLatest } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { BatteryDialoguesService } from '../../battery-dialogues.service';
import { compareBatteries } from '../../helpers';

@Component({
    selector: 'battery-set-edit',
    templateUrl: './battery-set-edit.component.html'
})
export class BatterySetEdit implements OnInit, OnDestroy, Cancellable {
    @Input() currentOrganisation: PersonsOrganisationDto;
    @Input() batterySet: BatterySetDto;

    @Output() updated = new EventEmitter<BatterySetDto>();
    @Output() cancelled = new EventEmitter<void>();

    private ngUnsubscribe$ = new Subject<void>();

    private workTracker = new WorkTracker();
    working = false;

    batterySetForm: FormGroup;
    error: any = null;
    lockedFields: LockedBatterySetFields;

    // Available batteries
    private batteries = new Subject<BatteryDto[]>();
    availableBatteries: BatteryDto[];
    // Do the batteries in the org have multiple owners
    multipleOwnersInOrg = false;

    selectedBatteryType = 'No batteries selected for this battery set';

    constructor(
        private batteryService: BatteryService,
        private batterySetService: BatterySetService,
        private commonDialoguesService: CommonDialoguesService,
        private batteryDialoguesService: BatteryDialoguesService,
        private logging: FlyFreelyLoggingService,
        @Optional() private screenAnalytics: ScreenAnalyticsDirective
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));
    }

    ngOnInit() {
        this.batterySetForm = new FormGroup({
            name: new FormControl(this.batterySet.name, [Validators.required]),
            assemblyDate: new FormControl(
                this.batterySet.assemblyDate
                    ? fromLocalDate(this.batterySet.assemblyDate)
                    : null,
                [Validators.required]
            ),
            disassemblyDate: new FormControl(
                this.batterySet.disassemblyDate
                    ? fromLocalDate(this.batterySet.disassemblyDate)
                    : null
            ),
            batteries: new FormArray(
                this.batterySet.batteries
                    .sort(compareBatteries)
                    .map(b => new FormControl(b))
            ),
            batterySearch: new FormControl(undefined)
        });

        this.lockedFields = this.batterySet.lockedFields;
        combineLatest([
            this.batteries,
            this.batterySetForm.valueChanges.pipe(
                startWith(this.batterySetForm.value)
            )
        ])
            .pipe(
                map(([batteries, value]) => {
                    const selectedIds = value.batteries.map(
                        (b: BatteryDto) => b.id
                    );
                    const batteryTypeId =
                        value.batteries.length > 0
                            ? value.batteries[0].batteryTypeId
                            : null;
                    if (batteryTypeId != null) {
                        this.selectedBatteryType = `${
                            (<BatteryDto>value.batteries[0]).batteryTypeMake &&
                            (<BatteryDto>value.batteries[0]).batteryTypeModel
                                ? (<BatteryDto>value.batteries[0])
                                      .batteryTypeMake
                                : ''
                        }${
                            (<BatteryDto>value.batteries[0]).batteryTypeMake
                                ? ' '
                                : ''
                        }${
                            (<BatteryDto>value.batteries[0]).batteryTypeModel ??
                            'Unknown make and model'
                        }`;
                    } else {
                        this.selectedBatteryType =
                            'No batteries selected for this battery set';
                    }
                    return batteries
                        .sort(compareBatteries)
                        .filter(
                            b =>
                                moment(b.purchaseDate).isSameOrBefore(
                                    value.assemblyDate
                                ) &&
                                selectedIds.indexOf(b.id) === -1 &&
                                (batteryTypeId === null ||
                                    b.batteryTypeId === batteryTypeId)
                        )
                        .filter(b =>
                            value.batterySearch == null ||
                            value.batterySearch === ''
                                ? true
                                : batterySearch(value.batterySearch, b)
                        );
                })
            )
            // This is done because *ngFor | async isn't working as expected
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(batteries => (this.availableBatteries = batteries));

        this.refreshBatteries();
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    refreshBatteries() {
        this.batteryService
            .findBatteries(this.currentOrganisation.id, 'SERVICEABLE')
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: batteries => {
                    this.multipleOwnersInOrg =
                        batteries.reduce(
                            (acc, bat) =>
                                acc.includes(bat.organisationId)
                                    ? acc
                                    : acc.concat(bat.organisationId),
                            []
                        ).length > 1;
                    this.batteries.next(batteries);
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error while loading batteries: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    extractId(battery: BatteryDto) {
        return battery.id;
    }

    showBatteryDetails(battery: BatteryDto) {
        this.batteryDialoguesService.showBatteryDetailsDialogue(
            battery,
            this.currentOrganisation
        );
    }

    addBattery(battery: BatteryDto) {
        (<FormArray>this.batterySetForm.controls.batteries).push(
            new FormControl(battery)
        );
        this.batterySetForm.updateValueAndValidity();
    }

    removeBattery(ix: number) {
        (<FormArray>this.batterySetForm.controls.batteries).removeAt(ix);
    }

    submit() {
        if (!this.doTypesMatch()) {
            this.error = 'Batteries that are selected are of different types';
            return;
        }
        const { name, assemblyDate, disassemblyDate, batteries } =
            this.batterySetForm.value;

        const payload: UpdateBatterySetCommand = {
            name: name,
            assemblyDate: toLocalDate(assemblyDate),
            disassemblyDate: toLocalDate(disassemblyDate),
            batteryIds: batteries.map(this.extractId)
        };

        this.batterySetService
            .updateBatterySet(this.batterySet.id, payload)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(
                results => {
                    this.logging.success(`Battery set updated`);
                    this.updated.emit(results);
                },
                (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error occurred while saving battery set: ${error.message}`
                    );
                }
            )
            .add(this.workTracker.createTracker());
    }

    doTypesMatch() {
        const batteries: BatteryDto[] = this.batterySet.batteries;
        const batteryResult = batteries.reduce((a, b) => {
            return a.batteryTypeId === b.batteryTypeId ? a : null;
        });
        const dummyResult = batteries.reduce((a, b) => {
            return a.isDummy === b.isDummy ? a : null;
        });
        if (batteryResult && dummyResult) {
            return true;
        } else {
            return false;
        }
    }

    canAddBattery(battery: BatteryDto) {
        const firstBatteryInSet = this.batterySet.batteries[0];
        if (firstBatteryInSet != null) {
            if (
                firstBatteryInSet.batteryTypeId === battery.batteryTypeId &&
                firstBatteryInSet.isDummy === battery.isDummy
            ) {
                return true;
            }
        } else {
            return true;
        }
        return false;
    }

    clearSearch() {
        this.batterySetForm.controls.batterySearch.reset();
    }

    confirmCancel() {
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Confirm Cancel',
                `You have unsaved changes, are you sure you want to cancel?`,
                'Yes',
                () => Promise.resolve()
            )
            .then(() => this.cancelEdit());
    }

    cancel() {
        if (this.batterySetForm.dirty) {
            this.confirmCancel();
        } else {
            this.cancelEdit();
        }
    }

    cancelEdit() {
        this.cancelled.emit();
    }
}
